import ApiClient from '../../core/ApiClient';
import {
    readClientId,
    readGclid,
    readMsclkid,
    readSessionId,
    sha256HexEmail,
} from '../../analytics/sendMakePurchaseWebhook.js';
import { resolveDefaultVehicleCountryCode } from '../../utils/languageDefaultCountry.js';
import {
    getCategoryCanonicalDescription,
    getCategoryDescription,
    getCategoryId,
    normalizeDurationKey
} from '../../components/BuyVignette/config/durationKeywords.js';

export const STEP_SELECT_VIGNETTE = 1;
const STEP_NAMES = ['select-vignette', 'enter-details', 'payment'];
const DISALLOWED_FORM_KEYS = new Set(['name']);

/** Step 1 owns vignette selection; step 2 validates only customer / vehicle / invoice fields. */
const STEP2_EXCLUDE_FROM_VALIDATE = new Set([
    'product_id',
    'crossing_type',
    'vehicle_type',
    'category_id',
    'selected_duration',
    'start_date',
    'end_date',
]);

const getDefaultFormData = () => ({
    /** Step 1 / backend `vehicle_type`: vehicle category id from GET /api/products/categories (row.id). */
    vehicle_type: null,
    /** Step 1 / backend `crossing_type`: selected vignette product id (duration product). */
    crossing_type: null,
    /** Locale-stable vignette duration group; matches product.category.id when API provides it. */
    category_id: null,
    product_id: null,
    selected_duration: null,
    start_date: '',

    plate_country: '',
    license_plate: '',
    license_plate_confirmation: '',
    email: '',
    email_confirmation: '',

    need_an_invoice: false,
    invoice_name: '',
    invoice_surname: '',
    invoice_company_name: '',
    invoice_vat_number: { country: '', number: '' },
    invoice_city: '',
    invoice_address: '',
    invoice_zipcode: '',
    invoice_country: '',

    payment_method: null,
    terms_condition: false,
    confirm_entered_data: false
});

const STEP_FIELDS = {
    step1: ['vehicle_type', 'crossing_type', 'start_date'],
    step2: [
        'plate_country',
        'license_plate',
        'license_plate_confirmation',
        'email',
        'email_confirmation',
        'need_an_invoice',
        'invoice_name',
        'invoice_surname',
        'invoice_company_name',
        'invoice_vat_number',
        'invoice_city',
        'invoice_address',
        'invoice_zipcode',
        'invoice_country'
    ],
    step3: ['payment_method', 'confirm_entered_data', 'terms_condition']
};

/** Values for `api_tags` on POST `/orders/make` (attribution + hashed email). */
async function buildOrderApiTags(email) {
    const gclid = readGclid();
    const msclkid = readMsclkid();
    return {
        gclid: gclid || '',
        msclkid: msclkid || '',
        sid: readSessionId(),
        cid: readClientId(),
        site: 'evignettehungary.com',
        /** SHA-256 hex of trimmed email; same algorithm as Make.com purchase webhook. */
        email: await sha256HexEmail(email),
    };
}

const STEP_TO_API_NUMBER = {
    step1: '1',
    step2: '2',
    step3: '3'
};

/** Keys used by buy-vignette flow; cleared when checkout completes so the next order starts at step 1. */
const BUY_FLOW_LOCAL_STORAGE_KEYS = [
    'vignetteFormData',
    'currentStep',
    'formData',
    'product_id',
];

/** Survives Mollie redirect when cancel URL omits `?token=` (sessionStorage, same tab). */
export const PENDING_ORDER_TOKEN_STORAGE_KEY = 'evh_pending_order_token';

export function readPendingOrderToken() {
    if (typeof sessionStorage === 'undefined') return null;
    try {
        const token = String(sessionStorage.getItem(PENDING_ORDER_TOKEN_STORAGE_KEY) || '').trim();
        return token || null;
    } catch {
        return null;
    }
}

export function persistPendingOrderToken(token) {
    const safe = String(token || '').trim();
    if (!safe || typeof sessionStorage === 'undefined') return;
    try {
        sessionStorage.setItem(PENDING_ORDER_TOKEN_STORAGE_KEY, safe);
    } catch { /* ignore */ }
}

export function clearPendingOrderToken() {
    if (typeof sessionStorage === 'undefined') return;
    try {
        sessionStorage.removeItem(PENDING_ORDER_TOKEN_STORAGE_KEY);
    } catch { /* ignore */ }
}

function extractOrderTokenFromResponseEnvelope(responseData) {
    const nodes = [
        responseData?.data,
        responseData?.data?.data,
        responseData,
    ].filter((n) => n != null && typeof n === 'object');

    for (const node of nodes) {
        const direct = node.token ?? node.order_token ?? node.access_token;
        if (direct != null && String(direct).trim() !== '') {
            return String(direct).trim();
        }
        const url = node.url ?? node.redirect_url ?? node.payment_url;
        if (url) {
            try {
                const parsed = new URL(
                    String(url),
                    typeof window !== 'undefined' ? window.location.origin : 'https://localhost'
                );
                const fromQuery = parsed.searchParams.get('token');
                if (fromQuery) return String(fromQuery).trim();
            } catch { /* ignore */ }
        }
    }
    return null;
}

/** Normalize POST `/orders/get/:token` envelopes into the order `data` object. */
function extractOrderPayloadFromGetResponse(responseData) {
    if (!responseData || typeof responseData !== 'object') return null;
    const nodes = [
        responseData.data,
        responseData.data?.data,
        responseData.result,
        responseData.result?.data,
        responseData,
    ].filter((n) => n != null && typeof n === 'object' && !Array.isArray(n));

    for (const node of nodes) {
        const hasOrderShape =
            node.form?.main
            || Array.isArray(node.orders)
            || node.payment
            || node.id != null;
        if (hasOrderShape) return node;
    }
    return null;
}

function getValidationFieldsForStep(state, stepKey) {
    const apiStep = STEP_TO_API_NUMBER[stepKey];
    const apiFields = (state.formFields || [])
        .filter((f) => String(f.step) === String(apiStep))
        .map((f) => f.key)
        .filter((key) => !DISALLOWED_FORM_KEYS.has(key));

    // Prefer server-driven step fields when available.
    if (apiFields.length) return apiFields;
    return (STEP_FIELDS[stepKey] || []).filter((key) => !DISALLOWED_FORM_KEYS.has(key));
}

/** GET /products/categories — proxied via POST /api with { method: GET, path }. */
const PRODUCTS_CATEGORIES_PATH = '/products/categories';

/**
 * Category endpoint may return `{ data: [...] }`, `{ data: { data: [...] } }`, `{ categories: [...] }`, etc.
 * Backend envelope example: `{ data: [...], message, status, type: "general_success" }` → use `body.data`.
 */
function extractCategorizedProductsRows(res) {
    const body = res?.data;
    if (!body) return [];
    if (Array.isArray(body)) return body;
    if (typeof body !== 'object') return [];
    if (Array.isArray(body.data)) return body.data;
    if (Array.isArray(body.result)) return body.result;
    if (Array.isArray(body.payload)) return body.payload;
    if (Array.isArray(body.records)) return body.records;
    const inner = body.data;
    if (inner && typeof inner === 'object') {
        if (Array.isArray(inner.data)) return inner.data;
        if (Array.isArray(inner.categories)) return inner.categories;
        if (Array.isArray(inner.items)) return inner.items;
    }
    if (Array.isArray(body.categories)) return body.categories;
    return [];
}

/**
 * Ensure each row has `id` and `products[]` (APIs may use category_id, items, vignettes, …).
 */
function normalizeCategorizedProductRows(rows) {
    if (!Array.isArray(rows)) return [];
    const out = [];
    for (const row of rows) {
        if (!row || typeof row !== 'object') continue;
        const id = row.id ?? row.category_id ?? row.categoryId;
        if (id == null || id === '') continue;
        const rawList =
            row.products
            ?? row.items
            ?? row.vignettes
            ?? row.product_list
            ?? row.products_list;
        const products = Array.isArray(rawList) ? rawList : [];
        out.push({
            ...row,
            id,
            products,
        });
    }
    return out;
}

/**
 * Nested products from GET /products/categories often omit `product.category`.
 * Attach parent row metadata so `getCategoryId` / `getCategoryDescription` stay consistent.
 */
function enrichCategorizedRowsWithProductCategories(rows) {
    if (!Array.isArray(rows)) return [];
    return rows.map((row) => {
        if (!row || typeof row !== 'object') return row;
        const parentCategory = {
            id: row.id,
            path: row.path,
            thumbnail: row.thumbnail,
            translated: row.translated,
            description: row.description,
        };
        const rawList = row.products;
        if (!Array.isArray(rawList)) return { ...row, products: [] };
        const products = rawList.map((p) => {
            if (!p || typeof p !== 'object') return p;
            const existing = p.category && typeof p.category === 'object' ? p.category : {};
            const hasId = existing.id != null && existing.id !== '';
            if (hasId && String(existing.id) === String(row.id)) return p;
            return {
                ...p,
                category_id: row.id,
                category: {
                    ...parentCategory,
                    ...existing,
                },
            };
        });
        return { ...row, products };
    });
}

/**
 * Flatten GET /products/categories into `state.products` so step 2/3, pricing, and
 * `autoSelectDefaultProduct` use the same product ids as step 1.
 */
function synchronizeStoreProductsWithCategorized(commit, state) {
    const rows = state.categorizedProducts || [];
    const flat = Array.isArray(state.products) ? state.products : [];
    if (!rows.length) return;

    const byId = new Map();
    for (const p of flat) {
        if (!p || p.id == null || p.id === '') continue;
        byId.set(String(p.id), { ...p });
    }

    for (const row of rows) {
        if (!row || row.id == null || row.id === '') continue;
        const parentCategory = {
            id: row.id,
            path: row.path,
            value: row.value,
            thumbnail: row.thumbnail,
            translated: row.translated,
            description: row.description,
            title: row.title,
        };

        for (const p of row.products || []) {
            if (!p || p.id == null || p.id === '') continue;
            const key = String(p.id);
            const existing = byId.get(key) || {};
            const childCat = p.category && typeof p.category === 'object' ? p.category : {};
            const merged = {
                ...existing,
                ...p,
                category: {
                    ...parentCategory,
                    ...childCat,
                },
                category_id: row.id,
                translated: {
                    ...(typeof existing.translated === 'object' && existing.translated
                        ? existing.translated
                        : {}),
                    ...(typeof p.translated === 'object' && p.translated ? p.translated : {}),
                    title:
                        p.translated?.title
                        ?? p.title
                        ?? existing.translated?.title
                        ?? existing.title
                        ?? p.name
                        ?? '',
                },
            };
            byId.set(key, merged);
        }
    }

    commit('SET_PRODUCTS', Array.from(byId.values()));
}

function flattenProductIconItems(rawItems) {
    if (Array.isArray(rawItems)) return rawItems;
    if (rawItems && typeof rawItems === 'object') {
        return Object.values(rawItems).flatMap((v) => (Array.isArray(v) ? v : []));
    }
    return [];
}

/** Optional CMS/API icon rows from GET /products/categories (`items` on envelope). */
function extractProductIconItemsFromResponse(res) {
    const body = res?.data;
    if (!body || typeof body !== 'object') return [];
    let raw = body.items;
    if (raw == null && body.data && typeof body.data === 'object' && !Array.isArray(body.data)) {
        raw = body.data.items;
    }
    return flattenProductIconItems(raw);
}

async function fetchCategorizedProductsFromApi(api) {
    try {
        const res = await api.request({ path: PRODUCTS_CATEGORIES_PATH });
        const extracted = extractCategorizedProductsRows(res);
        const rows = normalizeCategorizedProductRows(extracted);
        const iconItems = extractProductIconItemsFromResponse(res);
        return { rows, iconItems };
    } catch (e) {
        return { rows: [], iconItems: [] };
    }
}

async function loadCategorizedProductsResolved(api, commit) {
    const { rows: fetched, iconItems } = await fetchCategorizedProductsFromApi(api);
    const rows = enrichCategorizedRowsWithProductCategories(fetched);
    commit('SET_CATEGORIZED_PRODUCTS', rows);
    commit('SET_PRODUCT_ICON_ITEMS', iconItems);
    return rows;
}

function normalizeErrorFieldKey(rawKey) {
    const key = String(rawKey || '');
    let base = key;
    if (key.startsWith('form.main.')) base = key.replace('form.main.', '');
    else if (key.startsWith('main.')) base = key.replace('main.', '');
    else if (key.startsWith('form.')) base = key.replace('form.', '');
    if (base === 'vehicle_registration_country') base = 'plate_country';
    if (base === 'invoice_business_name') base = 'invoice_company_name';
    return base;
}

/** Keep `billing_data.first_name` keys intact for payment-step billing form. */
function normalizeOrderErrorFieldKey(rawKey) {
    const key = String(rawKey || '');
    if (key.startsWith('billing_data.')) return key;
    return normalizeErrorFieldKey(key);
}

export const BILLING_DATA_FIELDS = [
    'first_name',
    'last_name',
    'country',
    'city',
    'address',
    'zipcode',
    'company_name',
];

/** Payment methods that show the billing form on the payment step (Mollie BNPL / deferred). */
export const BILLING_REQUIRED_PAYMENT_METHODS = new Set([
    'billie',
    'in3',
    'klarna',
    'riverty',
]);

/**
 * @param {string | number | { value?: unknown, code?: unknown, name?: unknown, slug?: unknown }} codeOrMethod
 * API often uses numeric `value` ids; match by name/code substring too (e.g. name "Klarna").
 */
export function paymentMethodRequiresBilling(codeOrMethod) {
    const tokens = [];
    if (codeOrMethod != null && typeof codeOrMethod === 'object') {
        tokens.push(
            codeOrMethod.value,
            codeOrMethod.code,
            codeOrMethod.name,
            codeOrMethod.slug,
            codeOrMethod.key,
        );
    } else {
        tokens.push(codeOrMethod);
    }

    for (const raw of tokens) {
        const normalized = String(raw ?? '').trim().toLowerCase();
        if (!normalized) {
            continue;
        }
        if (BILLING_REQUIRED_PAYMENT_METHODS.has(normalized)) {
            return true;
        }
        for (const required of BILLING_REQUIRED_PAYMENT_METHODS) {
            if (normalized.includes(required)) {
                return true;
            }
        }
    }

    return false;
}

const getDefaultBillingData = () => ({
    first_name: '',
    last_name: '',
    country: '',
    city: '',
    address: '',
    zipcode: '',
    company_name: '',
});

function isBillingDataComplete(billingData = {}) {
    return BILLING_DATA_FIELDS.every((key) => String(billingData[key] ?? '').trim() !== '');
}

function buildBillingDataPayload(billingData = {}) {
    if (!isBillingDataComplete(billingData)) return null;
    const out = {};
    for (const key of BILLING_DATA_FIELDS) {
        out[key] = String(billingData[key] ?? '').trim();
    }
    return out;
}

function throwOrderValidationError(responseData) {
    const validationErrors = {};
    Object.keys(responseData.errors).forEach((key) => {
        validationErrors[normalizeOrderErrorFieldKey(key)] = true;
    });
    const err = new Error(responseData.message || 'Validation failed');
    err.validationErrors = validationErrors;
    throw err;
}

/** Same rules as EnterDetails `categoryProducts` filter (vignette products for selected vehicle category). */
function productBelongsToSelectedCategory(product, formData) {
    const fd = formData || {};
    const cid = fd.category_id;
    if (cid != null && cid !== '') {
        const pid = getCategoryId(product);
        if (pid == null || pid === '') return false;
        return String(pid) === String(cid);
    }
    const vt = String(fd.vehicle_type || '').toLowerCase().trim();
    if (!vt) return false;
    const localized = String(getCategoryDescription(product) || '').toLowerCase().trim();
    if (localized && localized === vt) return true;
    const canon = String(getCategoryCanonicalDescription(product) || '').toLowerCase().trim();
    if (canon && canon === vt) return true;
    const nk = (s) => normalizeDurationKey(s) || String(s || '').toLowerCase().trim();
    return nk(localized) === nk(vt) || nk(canon) === nk(vt);
}

/** Selected product id exists on the active category row from GET /api/products/categories. */
function categorizedProductMatchesSelection(state, rawId) {
    if (rawId == null || rawId === '') return false;
    const cid = state.formData?.category_id ?? state.formData?.vehicle_type;
    if (cid == null || cid === '') return false;
    const rows = state.categorizedProducts || [];
    const row = rows.find((r) => r && String(r.id) === String(cid));
    if (!row) return false;
    return (row.products || []).some((p) => p && String(p.id) === String(rawId));
}

function extractProductDuration(product) {
    const d = Number(product?.duration);
    if (Number.isFinite(d) && d > 0) return d;
    const match = product?.path && String(product.path).match(/(\d+)-day/);
    return match ? parseInt(match[1], 10) : 0;
}

/**
 * Products for the selected category from GET /api/products/categories — **same order as `row.products` in the API**
 * (e.g. 1-day, 10-day, monthly, annual). No client-side reordering.
 */
function getCategorizedRowProductsInApiOrder(state, formData) {
    const fd = formData || {};
    const cid = fd.category_id ?? fd.vehicle_type;
    if (cid == null || cid === '') return [];
    const rows = state.categorizedProducts || [];
    const row = rows.find((r) => r && String(r.id) === String(cid));
    if (!row || !Array.isArray(row.products)) return [];
    return row.products.filter((p) => p && p.enabled !== false && p.id != null);
}

/**
 * Flat `state.products` fallback: filter by category, sort by duration then id (no API bucket order).
 */
function getFlatCategoryProductsSortedByDuration(products, formData) {
    const list = Array.isArray(products) ? products : [];
    if (!list.length) return [];
    const fd = formData || {};
    const hasCategory =
        (fd.category_id != null && fd.category_id !== '')
        || String(fd.vehicle_type || '').trim();
    if (!hasCategory) return [];
    return list
        .filter((p) => productBelongsToSelectedCategory(p, fd))
        .sort((a, b) => {
            const da = extractProductDuration(a);
            const db = extractProductDuration(b);
            if (da !== db) return da - db;
            return Number(a.id) - Number(b.id);
        });
}

/** Prefer categorized API row order; otherwise flat-list duration sort. */
function getCategoryProductsInSelectionOrder(state, formData) {
    const fromApi = getCategorizedRowProductsInApiOrder(state, formData);
    if (fromApi.length) return fromApi;
    return getFlatCategoryProductsSortedByDuration(state.products || [], formData);
}

function normalizeInvoiceVatNumber(targetForm = {}) {
    if (!Object.prototype.hasOwnProperty.call(targetForm, 'invoice_vat_number')) return targetForm;
    const vatValue = targetForm.invoice_vat_number;
    const sanitizeVat = (value) =>
        String(value ?? '')
            .trim()
            .replace(/\s+/g, '')
            .toUpperCase();
    if (vatValue && typeof vatValue === 'object' && !Array.isArray(vatValue)) {
        targetForm.invoice_vat_number = sanitizeVat(vatValue.number);
        return targetForm;
    }
    targetForm.invoice_vat_number = sanitizeVat(vatValue);
    return targetForm;
}

function invoiceVatPayloadPatch(targetForm = {}) {
    if (!Object.prototype.hasOwnProperty.call(targetForm, 'invoice_vat_number')) return {};
    return { invoice_vat_number: String(targetForm.invoice_vat_number ?? '') };
}

function extractVatValidationError(responseData) {
    const message = String(responseData?.message ?? '');
    const exception = String(responseData?.exception ?? '');
    const looksLikeVatTypeError =
        exception === 'TypeError'
        && /VatValidator::validate/.test(message)
        && /must be of type string,\s*array given/i.test(message);

    const isViesConcurrentLimitError =
        /ViesException/.test(exception)
        && /MS_MAX_CONCURRENT_REQ/i.test(message);

    if (!looksLikeVatTypeError && !isViesConcurrentLimitError) return null;

    const userMessage = looksLikeVatTypeError
        ? 'Please enter a valid VAT number (e.g. DE129273398).'
        : 'VAT validation service is busy right now. Please try again in 1-2 minutes.';
    const err = new Error(userMessage);
    err.validationErrors = {
        invoice_vat_number: userMessage,
        'invoice_vat_number.number': userMessage,
    };
    return err;
}

/**
 * Some /forms/fields responses omit invoice_vat. Inject a step-2 text field
 * between company name and the next invoice field so it appears in EnterDetails
 * and is included in API-driven validation keys.
 */
function ensureInvoiceVatField(formFields) {
    if (!Array.isArray(formFields) || formFields.some((f) => f.key === 'invoice_vat_number')) {
        return formFields;
    }
    const step2 = formFields.filter((f) => String(f.step) === '2');
    const sorted = [...step2].sort((a, b) => (Number(a.index) || 0) - (Number(b.index) || 0));
    const companyNamePos = sorted.findIndex((f) => f.key === 'invoice_company_name');
    const companyNameField = companyNamePos >= 0 ? sorted[companyNamePos] : null;
    const next = companyNamePos >= 0 ? sorted[companyNamePos + 1] : null;

    let index = 100;
    if (companyNameField && next) {
        const bi = Number(companyNameField.index) || 0;
        const ni = Number(next.index) || bi + 10;
        index = bi + (ni - bi) / 2;
    } else if (companyNameField) {
        index = Number(companyNameField.index) + 5;
    } else {
        const invoiceBlock = sorted.filter((f) => f.key && f.key.startsWith('invoice_'));
        const last = invoiceBlock[invoiceBlock.length - 1];
        if (last) index = Number(last.index) + 5;
    }

    const vatField = {
        key: 'invoice_vat_number',
        type: 'vat_composite',
        step: '2',
        section_value: 'invoice',
        index,
        translated: {
            label: 'VAT number',
            placeholder: 'e.g. DE129273398',
        },
    };
    return [...formFields, vatField];
}

function pickStepData(formData, fields) {
    const result = {};
    fields.forEach((key) => {
        result[key] = formData[key];
    });
    return result;
}

/**
 * CMS `/api` proxy may return `{ data: { main, other } }` or wrap once more
 * (`data: { data: { main, other } }, status, type`).
 */
function extractSortedPaymentMethodsFromApiEnvelope(res) {
    const body = res?.data;
    if (!body || typeof body !== 'object') {
        return { main: [], other: [] };
    }

    const nodesToTry = [
        body,
        body.data,
        body.data?.data,
        body.result,
        body.result?.data,
    ].filter((n) => n != null);

    for (const node of nodesToTry) {
        if (Array.isArray(node)) {
            return { main: node, other: [] };
        }
        if (typeof node !== 'object') {
            continue;
        }
        const hasMainKey = Object.prototype.hasOwnProperty.call(node, 'main');
        const hasOtherKey = Object.prototype.hasOwnProperty.call(node, 'other');
        if (hasMainKey || hasOtherKey) {
            return {
                main: Array.isArray(node.main) ? node.main : [],
                other: Array.isArray(node.other) ? node.other : [],
            };
        }
    }
    return { main: [], other: [] };
}

/** Selected checkout currency code for POST /orders/make and /orders/amount (e.g. `"EUR"`). */
function resolveCheckoutCurrencyCode(rootGetters) {
    const code = String(rootGetters?.['currency/currentCurrency']?.code ?? '')
        .trim()
        .toUpperCase();
    return code || null;
}

/** POST /orders/amount — `{ data: { amount, sub_amount, payment_method_fee, currency, products } }`. */
function extractOrderAmountFromApiEnvelope(res) {
    const body = res?.data;
    if (!body || typeof body !== 'object') return null;
    const nodes = [body, body.data, body.data?.data, body.result, body.result?.data].filter(
        (n) => n != null && typeof n === 'object'
    );
    for (const node of nodes) {
        if (
            node.amount != null
            || node.sub_amount != null
            || node.payment_method_fee != null
        ) {
            return node;
        }
    }
    return null;
}

/** One shared load for ShoppingCart, BuyVignette, VignettePrices, etc. */
let initialDataLoaded = false;
let initialDataLoadPromise = null;

export const formModule = {
    namespaced: true,

    state: {
        formData: getDefaultFormData(),
        step: STEP_SELECT_VIGNETTE,
        countries: [],
        products: [],
        formFields: [],
        paymentMethodsMain: [],
        paymentMethodsOther: [],
        /** GET /payments/methods — How it works pills only (not checkout / payment-methods page). */
        paymentMethodsHowItWorks: [],
        loadingCount: 0,
        orderData: null,
        /** Latest POST /orders/amount for selected payment method (EUR base amounts). */
        orderAmount: null,
        orderAmountLoading: false,
        billingData: getDefaultBillingData(),
        allowedFormKeys: [],
        /** From GET /products/categories: res.data.items — optional CMS/API rows keyed by product */
        productIconItems: [],
        /** GET /products/categories — locale-aware category buckets (ShoppingCart duration labels). */
        categorizedProducts: [],
        /** root `lang.id` used for last successful /countries fetch (avoids stale list after language change). */
        countriesFetchLanguageId: null,
    },

    mutations: {
        SET_FORM_DATA(state, payload) {
            const today = new Date().toISOString().slice(0, 10);

            const sanitizedPayload = Object.fromEntries(
                Object.entries(payload || {}).filter(([key]) => !DISALLOWED_FORM_KEYS.has(key))
            );

            if (Object.prototype.hasOwnProperty.call(sanitizedPayload, 'vehicle_registration_country')) {
                if (sanitizedPayload.plate_country == null || sanitizedPayload.plate_country === '') {
                    sanitizedPayload.plate_country = sanitizedPayload.vehicle_registration_country ?? '';
                }
                delete sanitizedPayload.vehicle_registration_country;
            }

            if (sanitizedPayload.start_date && sanitizedPayload.start_date < today) {
                sanitizedPayload.start_date = today;
            }

            if (!sanitizedPayload.start_date && !state.formData.start_date) {
                sanitizedPayload.start_date = today;
            }

            const mergedForm = {
                ...state.formData,
                ...sanitizedPayload
            };
            if (Object.prototype.hasOwnProperty.call(mergedForm, 'invoice_business_name')) {
                if (mergedForm.invoice_company_name == null || mergedForm.invoice_company_name === '') {
                    mergedForm.invoice_company_name = mergedForm.invoice_business_name ?? '';
                }
                delete mergedForm.invoice_business_name;
            }
            state.formData = mergedForm;
        },
        SET_ALLOWED_FORM_KEYS(state, keys) {
            state.allowedFormKeys = keys;
        },
        CLEAR_FORM_DATA(state) {
            try {
                BUY_FLOW_LOCAL_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
            } catch (e) {}
            clearPendingOrderToken();
            state.formData = getDefaultFormData();
            state.step = STEP_SELECT_VIGNETTE;
            state.paymentMethodsMain = [];
            state.paymentMethodsOther = [];
            state.paymentMethodsHowItWorks = [];
            state.orderAmount = null;
            state.orderAmountLoading = false;
            state.billingData = getDefaultBillingData();
        },

        SET_BILLING_DATA(state, payload) {
            state.billingData = {
                ...getDefaultBillingData(),
                ...state.billingData,
                ...(payload && typeof payload === 'object' ? payload : {}),
            };
        },

        SET_STEP(state, payload) {
            const n = typeof payload === 'number' ? payload : (STEP_NAMES.indexOf(payload) + 1) || STEP_SELECT_VIGNETTE;
            state.step = n >= 1 && n <= 3 ? n : STEP_SELECT_VIGNETTE;
        },

        SET_COUNTRIES(state, countries) {
            state.countries = countries;
            // console.log('[Vuex] countries loaded:', state.countries);
        },

        SET_COUNTRIES_FETCH_LANGUAGE_ID(state, languageId) {
            state.countriesFetchLanguageId =
                languageId === undefined || languageId === null || languageId === ''
                    ? null
                    : languageId;
        },

        SET_PRODUCTS(state, products) {
            state.products = products;
            // console.log('[Vuex] products loaded:', state.products);
        },

        SET_PRODUCT_ICON_ITEMS(state, items) {
            state.productIconItems = Array.isArray(items) ? items : [];
        },

        SET_CATEGORIZED_PRODUCTS(state, rows) {
            state.categorizedProducts = Array.isArray(rows) ? rows : [];
        },

        SET_FORM_FIELDS(state, formFields) {
            state.formFields = formFields;
            // console.log('[Vuex] formFields loaded:', state.formFields);
        },

        SET_PAYMENT_METHODS_SORTED(state, { main = [], other = [] } = {}) {
            state.paymentMethodsMain = Array.isArray(main) ? main : [];
            state.paymentMethodsOther = Array.isArray(other) ? other : [];
        },

        SET_PAYMENT_METHODS_HOW_IT_WORKS(state, methods) {
            state.paymentMethodsHowItWorks = Array.isArray(methods) ? methods : [];
        },

        SET_ORDER_DATA(state, payload) {
            state.orderData = payload;
            // console.log('[Vuex] orderData loaded:', state.orderData);
        },

        SET_ORDER_AMOUNT(state, payload) {
            state.orderAmount = payload && typeof payload === 'object' ? payload : null;
        },

        SET_ORDER_AMOUNT_LOADING(state, loading) {
            state.orderAmountLoading = !!loading;
        },
    },

    actions: {
        /**
         * Align license-plate country with the current site language (see LANGUAGE_TO_COUNTRY).
         * Mirrors ShoppingCart.syncCountryToLanguage — clears plate fields when the country changes.
         * @param {{ force?: boolean }} [options] when force is true, still run when country already matches (e.g. language switch)
         */
        syncVehicleCountryToLanguage({ state, commit, rootState, dispatch }, options = {}) {
            const force = options && options.force === true;
            const countries = state.countries || [];
            if (!countries.length) return;

            // const langCode = rootState?.lang?.code;
            const rawLangCode = String(rootState?.lang?.code || '').toLowerCase();
            let langCode = rawLangCode;
            if (rawLangCode === 'ro-md' || rawLangCode === 'moldova-ro' || rawLangCode === 'ro_md') {
                langCode = 'md';
            }

            /**
             * Language-switch refetches /countries and re-runs this action. We must not clear
             * license plate or replace plate country once the user has progressed in the flow
             * or has entered any plate text — otherwise step 2/3 data disappears on locale change.
             */
            const plateEntered =
                String(state.formData?.license_plate || '').trim() !== ''
                || String(state.formData?.license_plate_confirmation || '').trim() !== '';
            const pastStepOne = Number(state.step) >= 2;
            if (plateEntered || pastStepOne) {
                try {
                    const normalizedLang = String(langCode || '').toLowerCase().trim();
                    if (normalizedLang) localStorage.setItem('cartLangCodeApplied', normalizedLang);
                } catch (e) { /* ignore */ }
                return;
            }

            const targetCountry = resolveDefaultVehicleCountryCode(langCode, countries);
            if (!targetCountry) return;

            const current = state.formData?.plate_country || '';
            if (!force && current === targetCountry) {
                try {
                    const normalizedLang = String(langCode || '').toLowerCase().trim();
                    if (normalizedLang) localStorage.setItem('cartLangCodeApplied', normalizedLang);
                } catch (e) { /* ignore */ }
                return;
            }

            commit('SET_FORM_DATA', {
                plate_country: targetCountry,
                license_plate: '',
                license_plate_confirmation: ''
            });
            try {
                const normalizedLang = String(langCode || '').toLowerCase().trim();
                if (normalizedLang) localStorage.setItem('cartLangCodeApplied', normalizedLang);
            } catch (e) { /* ignore */ }
            return dispatch('saveDataToLocalStorage');
        },

        loadSavedData({ commit }) {
            const savedData = localStorage.getItem("vignetteFormData");

            if (savedData) {
                try {
                    const parsed = JSON.parse(savedData);
                    const sanitizedParsed = Object.fromEntries(
                        Object.entries(parsed || {}).filter(([key]) => !DISALLOWED_FORM_KEYS.has(key))
                    );
                    const merged = {
                        ...getDefaultFormData(),
                        ...sanitizedParsed,
                    };

                    if (Object.prototype.hasOwnProperty.call(merged, 'vehicle_registration_country')) {
                        if (merged.plate_country == null || merged.plate_country === '') {
                            merged.plate_country = merged.vehicle_registration_country ?? '';
                        }
                        delete merged.vehicle_registration_country;
                    }

                    const today = new Date().toISOString().slice(0, 10);

                    if (merged.start_date && merged.start_date < today) {
                        merged.start_date = today;
                    }

                    if (!merged.start_date) {
                        merged.start_date = today;
                    }

                    // Legacy: flat invoice_vat string → composite { country, number }
                    if (Object.prototype.hasOwnProperty.call(merged, 'invoice_vat')) {
                        if (merged.invoice_vat) {
                            merged.invoice_vat_number = {
                                country: merged.invoice_vat_number?.country ?? '',
                                number: String(merged.invoice_vat),
                            };
                        }
                        delete merged.invoice_vat;
                    }
                    if (
                        !merged.invoice_vat_number
                        || typeof merged.invoice_vat_number !== 'object'
                        || Array.isArray(merged.invoice_vat_number)
                    ) {
                        merged.invoice_vat_number = { country: '', number: '' };
                    } else {
                        merged.invoice_vat_number = {
                            country: merged.invoice_vat_number.country ?? '',
                            number: merged.invoice_vat_number.number ?? '',
                        };
                    }

                    if (Object.prototype.hasOwnProperty.call(merged, 'invoice_business_name')) {
                        if (merged.invoice_company_name == null || merged.invoice_company_name === '') {
                            merged.invoice_company_name = merged.invoice_business_name ?? '';
                        }
                        delete merged.invoice_business_name;
                    }

                    commit("SET_FORM_DATA", merged);
                } catch (e) {
                    console.error("Failed to parse saved data", e);
                }
            }
        },

        saveDataToLocalStorage({ state }) {
            try {
                localStorage.setItem("vignetteFormData", JSON.stringify(state.formData));
            } catch (e) {
                console.error("Failed to save data to localStorage", e);
            }
        },

        /**
         * Default product_id for the selected vehicle category (EnterDetails vignette list order).
         * Does not override a valid existing product_id. Clears stale ids that no longer match the category.
         */
        async autoSelectDefaultProduct({ state, commit, dispatch }) {
            const rawId = state.formData.product_id ?? state.formData.crossing_type;
            const hasId = rawId != null && rawId !== '';

            const ordered = getCategoryProductsInSelectionOrder(state, state.formData);

            if (!ordered.length) {
                if (hasId && categorizedProductMatchesSelection(state, rawId)) {
                    return;
                }
                if (hasId) {
                    commit('SET_FORM_DATA', {
                        product_id: null,
                        crossing_type: null,
                        selected_duration: null
                    });
                    await dispatch('saveDataToLocalStorage');
                }
                return;
            }

            if (hasId) {
                const stillValid = ordered.some(
                    (p) => p.id === rawId || String(p.id) === String(rawId)
                );
                if (stillValid) return;
                if (categorizedProductMatchesSelection(state, rawId)) {
                    return;
                }
                commit('SET_FORM_DATA', {
                    product_id: null,
                    crossing_type: null,
                    selected_duration: null
                });
            }

            const fd = state.formData;
            if (fd.product_id != null && fd.product_id !== '') return;

            const first = ordered[0];
            const duration = extractProductDuration(first);
            const catId = getCategoryId(first);
            const payload = {
                product_id: first.id,
                crossing_type: first.id,
                selected_duration: duration || null,
                category_id: catId != null && catId !== '' ? catId : fd.category_id
            };
            if (catId != null && catId !== '') {
                payload.vehicle_type = catId;
            } else {
                const vt = getCategoryDescription(first);
                if (vt) payload.vehicle_type = vt;
            }
            commit('SET_FORM_DATA', payload);
            await dispatch('saveDataToLocalStorage');
        },

        async validateStep({ state }, stepKey) {
            let fields = getValidationFieldsForStep(state, stepKey);

            if (!fields) {
                throw new Error(`Unknown step: ${stepKey}`);
            }
            fields = [...new Set(fields.map(k => (k === 'vehicle_registration_country' ? 'plate_country' : k)))];
            if (stepKey === 'step2') {
                fields = fields.filter((k) => !STEP2_EXCLUDE_FROM_VALIDATE.has(k));
            }
            if (stepKey === 'step2' && !fields.includes('invoice_vat_number')) {
                fields = [...fields, 'invoice_vat_number'];
            }
            if (stepKey === 'step2' && !fields.includes('plate_country')) {
                fields = [...fields, 'plate_country'];
            }

            const stepForm = pickStepData(state.formData, fields);
            const filteredStepForm = Object.keys(stepForm)
                .filter(key => state.allowedFormKeys.includes(key))
                .filter(key => !DISALLOWED_FORM_KEYS.has(key))
                .reduce((obj, key) => {
                    obj[key] = stepForm[key];
                    return obj;
                }, {});
            if (stepKey === 'step2') {
                filteredStepForm.plate_country =
                    state.formData.plate_country
                    ?? filteredStepForm.plate_country
                    ?? '';
            }

            if (Object.prototype.hasOwnProperty.call(filteredStepForm, 'invoice_business_name')) {
                if (
                    filteredStepForm.invoice_company_name == null
                    || filteredStepForm.invoice_company_name === ''
                ) {
                    filteredStepForm.invoice_company_name =
                        filteredStepForm.invoice_business_name ?? '';
                }
                delete filteredStepForm.invoice_business_name;
            }

            if (stepKey === 'step2' && !filteredStepForm.need_an_invoice) {
                delete filteredStepForm.invoice_name;
                delete filteredStepForm.invoice_surname;
                delete filteredStepForm.invoice_business_name;
                delete filteredStepForm.invoice_company_name;
                delete filteredStepForm.invoice_vat_number;
                delete filteredStepForm.invoice_city;
                delete filteredStepForm.invoice_address;
                delete filteredStepForm.invoice_zipcode;
                delete filteredStepForm.invoice_country;
            }
            normalizeInvoiceVatNumber(filteredStepForm);

            const res = await new ApiClient().request({
                method: 'POST',
                path: '/forms/validate',
                data: {
                    step: stepKey,
                    // form: filteredStepForm
                    form: {
                        main: {
                            ...filteredStepForm,
                            ...invoiceVatPayloadPatch(filteredStepForm),
                        }
                    }
                }
            });

            const responseData = res?.data;

            if (responseData?.errors && Object.keys(responseData.errors).length) {
                const validationErrors = {};

                Object.keys(responseData.errors).forEach((key) => {
                    const fieldKey = normalizeErrorFieldKey(key);
                    validationErrors[fieldKey] = true;
                });

                const err = new Error(responseData.message || 'Validation failed');
                err.validationErrors = validationErrors;
                throw err;
            }

            const vatTypeErr = extractVatValidationError(responseData);
            if (vatTypeErr) {
                throw vatTypeErr;
            }

            if (responseData?.exception) {
                throw new Error(responseData?.message || 'Validation failed');
            }

            return responseData;
        },

        async createOrder({ state, commit, rootGetters }) {
            const formData = state.formData;
            const rawProductId = formData.product_id ?? formData.crossing_type;
            const product_id =
                rawProductId != null && rawProductId !== ''
                    ? (Number.isFinite(Number(rawProductId)) ? Number(rawProductId) : rawProductId)
                    : null;
            const { payment_method, need_an_invoice } = formData;

            const defaults = getDefaultFormData();
            let cleanForm = state.allowedFormKeys
                .filter(key => !DISALLOWED_FORM_KEYS.has(key))
                .reduce((obj, key) => {
                    let val = formData[key];
                    if (val === undefined && Object.prototype.hasOwnProperty.call(defaults, key)) {
                        val = defaults[key];
                    }
                    if (val !== undefined) {
                        obj[key] = val;
                    }
                    return obj;
                }, {});
            cleanForm.plate_country =
                formData.plate_country ?? cleanForm.plate_country ?? '';

            if (Object.prototype.hasOwnProperty.call(cleanForm, 'invoice_business_name')) {
                if (cleanForm.invoice_company_name == null || cleanForm.invoice_company_name === '') {
                    cleanForm.invoice_company_name = cleanForm.invoice_business_name ?? '';
                }
                delete cleanForm.invoice_business_name;
            }

            if (!need_an_invoice) {
                delete cleanForm.invoice_name;
                delete cleanForm.invoice_surname;
                delete cleanForm.invoice_business_name;
                delete cleanForm.invoice_company_name;
                delete cleanForm.invoice_vat_number;
                delete cleanForm.invoice_city;
                delete cleanForm.invoice_address;
                delete cleanForm.invoice_zipcode;
                delete cleanForm.invoice_country;
            }
            normalizeInvoiceVatNumber(cleanForm);

            delete cleanForm.phone;

            if (product_id == null || product_id === '') {
                const err = new Error('No vignette product selected. Please go back and choose a duration.');
                err.validationErrors = { crossing_type: true, product_id: true };
                throw err;
            }

            const currency = resolveCheckoutCurrencyCode(rootGetters);
            const billing_data = buildBillingDataPayload(state.billingData);
            const orderData = {
                products: [{ id: product_id, quantity: 1 }],
                payment_method,
                ...(currency ? { currency } : {}),
                form: {
                    main: {
                        ...cleanForm,
                        ...invoiceVatPayloadPatch(cleanForm),
                        need_an_invoice
                    }
                },
                ...(billing_data ? { billing_data } : {}),
                api_tags: await buildOrderApiTags(formData.email),
            };

            try {
                const res = await new ApiClient().request({
                    method: 'POST',
                    path: '/orders/make',
                    data: orderData,
                });

                const responseData = res?.data;
                if (responseData?.errors && Object.keys(responseData.errors).length) {
                    throwOrderValidationError(responseData);
                }

                commit('SET_ORDER_DATA', res.data);
                const orderToken = extractOrderTokenFromResponseEnvelope(res.data);
                if (orderToken) persistPendingOrderToken(orderToken);
                return res.data;
            } catch (e) {
                if (e?.validationErrors) throw e;
                const responseData = e?.response?.data;
                if (responseData?.errors && Object.keys(responseData.errors).length) {
                    throwOrderValidationError(responseData);
                }
                throw e;
            }
        },

        /**
         * POST /orders/amount — product total + payment-method fee for checkout display.
         * @param {string|null} [paymentMethodOverride]
         */
        async fetchOrderAmount({ state, commit, rootGetters }, paymentMethodOverride = null) {
            const payment_method =
                paymentMethodOverride != null && paymentMethodOverride !== ''
                    ? paymentMethodOverride
                    : state.formData?.payment_method;
            if (payment_method == null || payment_method === '') {
                commit('SET_ORDER_AMOUNT', null);
                return null;
            }

            const rawProductId = state.formData?.product_id ?? state.formData?.crossing_type;
            const product_id =
                rawProductId != null && rawProductId !== ''
                    ? (Number.isFinite(Number(rawProductId)) ? Number(rawProductId) : rawProductId)
                    : null;
            if (product_id == null || product_id === '') {
                commit('SET_ORDER_AMOUNT', null);
                return null;
            }

            commit('SET_ORDER_AMOUNT_LOADING', true);
            try {
                const currency = resolveCheckoutCurrencyCode(rootGetters);
                const res = await new ApiClient().request({
                    method: 'POST',
                    path: '/orders/amount',
                    data: {
                        products: [{ id: product_id, quantity: 1 }],
                        payment_method,
                        ...(currency ? { currency } : {}),
                    },
                });

                const data = extractOrderAmountFromApiEnvelope(res);
                if (!data) {
                    commit('SET_ORDER_AMOUNT', null);
                    return null;
                }

                const amount = Number(data.amount);
                const sub_amount = Number(data.sub_amount);
                const payment_method_fee = Number(data.payment_method_fee);
                const payload = {
                    amount: Number.isFinite(amount) ? amount : 0,
                    sub_amount: Number.isFinite(sub_amount) ? sub_amount : 0,
                    payment_method_fee: Number.isFinite(payment_method_fee) ? payment_method_fee : 0,
                    currency: data.currency ?? null,
                    payment_method,
                };
                commit('SET_ORDER_AMOUNT', payload);
                return payload;
            } catch (e) {
                console.warn('[form] fetchOrderAmount failed', e);
                commit('SET_ORDER_AMOUNT', null);
                throw e;
            } finally {
                commit('SET_ORDER_AMOUNT_LOADING', false);
            }
        },

        /**
         * Refetch GET /countries (via POST /api) using current language id for translated names.
         */
        async reloadCountries({ commit, dispatch, rootState }) {
            const api = new ApiClient();
            const res = await api.request({ path: '/countries' });
            const countries = (res.data.data || []).map(c => ({
                code: c.code,
                dial_code: c.dial_code,
                flag: c.flag,
                translated: { name: c.translated?.name || c.code }
            }));
            commit('SET_COUNTRIES', countries);
            commit('SET_COUNTRIES_FETCH_LANGUAGE_ID', rootState.lang?.id ?? null);
            await dispatch('syncVehicleCountryToLanguage');
            return countries;
        },

        /**
         * Loads countries, products, form fields, payment methods once per session
         * (deduped across all components). Pass { force: true } to refetch.
         */
        async loadInitialData({ commit, state, dispatch, rootState }, options = {}) {
            const force = options && options.force === true;
            const skipAutoSelect = options && options.skipAutoSelect === true;
            if (force) {
                initialDataLoaded = false;
                initialDataLoadPromise = null;
            }
            if (!force && initialDataLoaded) {
                const currentLangId = rootState.lang?.id;
                const fetchedFor = state.countriesFetchLanguageId;
                const hasCountries = state.countries && state.countries.length;
                const langMismatch =
                    hasCountries
                    && currentLangId != null
                    && (fetchedFor == null
                        || String(fetchedFor) !== String(currentLangId));
                if (langMismatch) {
                    return dispatch('reloadCountries');
                }
                return dispatch('syncVehicleCountryToLanguage');
            }
            if (!force && initialDataLoadPromise) {
                return initialDataLoadPromise;
            }

            const run = async () => {
                const api = new ApiClient();
                const langIdForCountries = rootState.lang?.id ?? null;

                const loadCountries = async () => {
                    const res = await api.request({ path: '/countries' });
                    const countries = (res.data.data || []).map(c => ({
                        code: c.code,
                        dial_code: c.dial_code,
                        flag: c.flag,
                        translated: { name: c.translated?.name || c.code }
                    }));
                    commit('SET_COUNTRIES', countries);
                    commit('SET_COUNTRIES_FETCH_LANGUAGE_ID', langIdForCountries);
                    return countries;
                };

                const loadFormFields = async (countries) => {
                    const res = await api.request({ path: '/forms/fields' });
                    const formFields = ensureInvoiceVatField((res.data.data || []).map(field => {
                        const f = { ...field };
                        if (f.key === 'vehicle_registration_country') f.key = 'plate_country';
                        if (f.key === 'invoice_business_name') f.key = 'invoice_company_name';
                        if (['plate_country', 'invoice_country'].includes(f.key)) {
                            f.options = countries || [];
                        }
                        return f;
                    }));
                    commit('SET_FORM_FIELDS', formFields);
                    const apiKeys = formFields
                        .map(f => f.key)
                        .filter((key) => !DISALLOWED_FORM_KEYS.has(key));
                    const systemKeys = Object.keys(getDefaultFormData())
                        .filter(key => !apiKeys.includes(key));
                    /** Always allow plate country (API field list can omit it; createOrder must send it). */
                    commit('SET_ALLOWED_FORM_KEYS', [...new Set([...apiKeys, ...systemKeys, 'plate_country'])]);
                    return formFields;
                };

                let countries = [];
                try { countries = await loadCountries(); } catch (e) { /* partial load: skip */ }
                await dispatch('syncVehicleCountryToLanguage');
                const loadCategorizedProducts = async () =>
                    loadCategorizedProductsResolved(api, commit);

                try { await loadCategorizedProducts(); } catch (e) { /* partial load: skip */ }
                synchronizeStoreProductsWithCategorized(commit, state);
                if (!skipAutoSelect) {
                    await dispatch('autoSelectDefaultProduct');
                }
                try { await loadFormFields(countries); } catch (e) { /* partial load: skip */ }
            };

            initialDataLoadPromise = run()
                .then(() => {
                    const has =
                        (state.countries && state.countries.length) ||
                        (state.products && state.products.length) ||
                        (state.formFields && state.formFields.length);
                    if (has) {
                        initialDataLoaded = true;
                    }
                })
                .finally(() => {
                    initialDataLoadPromise = null;
                });

            return initialDataLoadPromise;
        },

        async loadCategorizedProducts({ commit, state }) {
            const api = new ApiClient();
            const rows = await loadCategorizedProductsResolved(api, commit);
            synchronizeStoreProductsWithCategorized(commit, state);
            return rows;
        },

        /**
         * Full payment methods list for How it works page (GET /payments/methods).
         */
        async loadPaymentMethodsForHowItWorks({ commit }) {
            const api = new ApiClient();
            const res = await api.request({
                method: 'GET',
                path: '/payments/methods',
            });
            const { main, other } = extractSortedPaymentMethodsFromApiEnvelope(res);
            const methods = [...main, ...other];
            commit('SET_PAYMENT_METHODS_HOW_IT_WORKS', methods);
            return methods;
        },

        /**
         * Sorted payment methods for marketing blocks (payment-methods grid).
         * Restores cart country when present; otherwise uses language default after catalog load.
         */
        async loadPaymentMethodsForDisplay({ state, dispatch, rootState }) {
            try {
                await dispatch('loadSavedData');
            } catch (e) { /* ignore */ }

            const hasCatalog =
                (state.countries && state.countries.length > 0)
                || (state.products && state.products.length > 0);
            if (!hasCatalog) {
                try {
                    await dispatch('loadInitialData');
                } catch (e) {
                    return { main: [], other: [] };
                }
            }

            let country = String(state.formData?.plate_country ?? '').trim().toUpperCase();
            if (!country) {
                const langCode = rootState.lang?.code;
                country = String(
                    resolveDefaultVehicleCountryCode(langCode, state.countries || [])
                ).trim().toUpperCase();
            }

            if (!country) {
                return dispatch('loadPaymentMethodsSorted');
            }
            return dispatch('loadPaymentMethodsSorted', { country });
        },

        async loadPaymentMethodsSorted({ commit, state, dispatch }, options = {}) {
            const fromOptions =
                options && options.country != null && String(options.country).trim() !== ''
                    ? String(options.country).trim().toUpperCase()
                    : '';
            const country =
                fromOptions || String(state.formData?.plate_country ?? '').trim().toUpperCase();
            if (!country) {
                commit('SET_PAYMENT_METHODS_SORTED', { main: [], other: [] });
                await dispatch('saveDataToLocalStorage');
                return { main: [], other: [] };
            }

            const api = new ApiClient();
            const res = await api.request({
                method: 'GET',
                path: '/payments/methods/sorted',
                data: {
                    country: country,
                },
            });

            const { main, other } = extractSortedPaymentMethodsFromApiEnvelope(res);

            commit('SET_PAYMENT_METHODS_SORTED', { main, other });

            const all = [...main, ...other];
            const pm = state.formData?.payment_method;
            if (
                pm != null && pm !== ''
                && !all.some((m) => String(m?.value ?? m?.code ?? '') === String(pm))
            ) {
                commit('SET_FORM_DATA', { payment_method: null });
                commit('SET_ORDER_AMOUNT', null);
            }
            await dispatch('saveDataToLocalStorage');
            return { main, other };
        },

        async getOrderByToken({ commit }, token) {
            const safeToken = encodeURIComponent(String(token || '').trim());
            const res = await new ApiClient().request({
                method: 'POST',
                path: `/orders/get/${safeToken}`
            });

            commit('SET_ORDER_DATA', res.data);
            return res.data;
        },

        async prefillFromOrderToken({ commit, state, dispatch }, orderToken) {
            const token = String(orderToken || '').trim();
            if (!token) return null;

            const responseData = await dispatch('getOrderByToken', token);
            const payload = extractOrderPayloadFromGetResponse(responseData);
            if (!payload) {
                console.warn('[form] prefillFromOrderToken ignored unsuccessful response', responseData);
                return null;
            }

            const main = payload.form?.main || {};
            const allowedKeys = new Set(Object.keys(getDefaultFormData()));
            const patch = {};

            for (const [key, value] of Object.entries(main)) {
                if (!allowedKeys.has(key)) continue;
                if (DISALLOWED_FORM_KEYS.has(key)) continue;
                patch[key] = value;
            }

            if (patch.email && !patch.email_confirmation) {
                patch.email_confirmation = patch.email;
            }
            if (patch.license_plate && !patch.license_plate_confirmation) {
                patch.license_plate_confirmation = patch.license_plate;
            }

            const paymentMethod =
                payload.payment_method
                ?? payload.payment?.method?.value
                ?? payload.payment?.method?.code
                ?? null;
            if (paymentMethod != null && paymentMethod !== '') {
                patch.payment_method = paymentMethod;
            }

            const firstOrder = Array.isArray(payload.orders) ? payload.orders[0] : null;
            const firstProduct = Array.isArray(payload.products) ? payload.products[0] : null;
            const pageIdRaw = firstOrder?.page?.id ?? firstProduct?.page_id ?? null;
            const productIdRaw = firstProduct?.id ?? null;

            let catalogProduct = null;
            let categoryBucketId = null;
            const rows = state.categorizedProducts || [];

            for (const row of rows) {
                const product = (row?.products || []).find((p) => {
                    if (!p) return false;
                    if (pageIdRaw != null && String(p.page_id) === String(pageIdRaw)) return true;
                    return productIdRaw != null && String(p.id) === String(productIdRaw);
                });
                if (product) {
                    catalogProduct = product;
                    categoryBucketId = row.id;
                    break;
                }
            }

            if (!catalogProduct && productIdRaw != null) {
                catalogProduct = (state.products || []).find(
                    (p) => p && String(p.id) === String(productIdRaw)
                );
            }
            if (!catalogProduct && pageIdRaw != null) {
                catalogProduct = (state.products || []).find(
                    (p) => p && String(p.page_id) === String(pageIdRaw)
                );
            }

            if (catalogProduct) {
                const catId = getCategoryId(catalogProduct) ?? categoryBucketId;
                const duration = extractProductDuration(catalogProduct) || null;
                patch.product_id = catalogProduct.id;
                patch.crossing_type = catalogProduct.id;
                patch.selected_duration = duration;
                if (catId != null && catId !== '') {
                    patch.category_id = catId;
                    patch.vehicle_type = catId;
                } else {
                    patch.vehicle_type = getCategoryDescription(catalogProduct) || patch.vehicle_type || null;
                }
            } else if (productIdRaw != null) {
                patch.product_id = productIdRaw;
                patch.crossing_type = productIdRaw;
            }

            if (Object.keys(patch).length) {
                commit('SET_FORM_DATA', patch);
            }

            const currencyCode =
                (typeof payload.currency === 'string' ? payload.currency : payload.currency?.code)
                ?? payload.payment?.currency?.code
                ?? payload.payment?.paid_currency?.code
                ?? null;
            if (currencyCode) {
                await dispatch('currency/setCurrencyByCode', currencyCode, { root: true }).catch(() => {});
            }

            if (patch.plate_country) {
                await dispatch('loadPaymentMethodsSorted', { country: patch.plate_country }).catch(() => {});
            }

            if (patch.payment_method) {
                await dispatch('fetchOrderAmount').catch(() => {});
            }

            await dispatch('saveDataToLocalStorage');

            return { patch, raw: payload };
        },

    },

    getters: {
        formData: state => state.formData,
        step: state => state.step,
        stepName: state => STEP_NAMES[state.step - 1] || 'select-vignette',
        countries: state => state.countries,
        products: state => state.products,
        categorizedProducts: state => state.categorizedProducts || [],
        productIconItems: state => state.productIconItems || [],
        formFields: state => state.formFields,
        paymentMethods: (state) => [
            ...(Array.isArray(state.paymentMethodsMain) ? state.paymentMethodsMain : []),
            ...(Array.isArray(state.paymentMethodsOther) ? state.paymentMethodsOther : []),
        ],
        paymentMethodsMain: (state) => state.paymentMethodsMain || [],
        paymentMethodsOther: (state) => state.paymentMethodsOther || [],
        paymentMethodsHowItWorks: (state) => state.paymentMethodsHowItWorks || [],
        loading: state => state.loadingCount > 0,
        orderData: state => state.orderData,
        orderAmount: (state) => state.orderAmount,
        orderAmountLoading: (state) => state.orderAmountLoading,
        billingData: (state) => state.billingData,
    },
};
